import React, { useState, useEffect, useRef, useId, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic, MicOff, Search } from 'lucide-react';
import { DictionaryService } from '../services/dictionaryService';

// Turns what people actually type ("what does petrichor mean",
// "another word for brave", "resilience ko arth") into the single word to look up
export const extractWord = (searchQuery) => {
  const cleaned = searchQuery.toLowerCase().trim()
    // English phrases (start)
    .replace(/^what does\s+/i, '')
    .replace(/^what is\s+/i, '')
    .replace(/^whats\s+/i, '')
    .replace(/^what['’]s\s+/i, '')
    .replace(/^tell me\s+/i, '')
    .replace(/^give me\s+/i, '')
    .replace(/^please\s+/i, '')
    .replace(/^can you tell me\s+/i, '')
    .replace(/^i want to know\s+/i, '')
    .replace(/^explain\s+/i, '')
    .replace(/^define\s+/i, '')
    .replace(/^show\s+/i, '')
    .replace(/^search\s+/i, '')
    .replace(/^find\s+/i, '')
    .replace(/^look up\s+/i, '')
    .replace(/^meaning of\s+/i, '')
    .replace(/^definition of the\s+/i, '')
    .replace(/^definition of\s+/i, '')
    .replace(/^defination of\s+/i, '') // misspelling
    .replace(/^what is the meaning of\s+/i, '')
    .replace(/^what's the meaning of\s+/i, '')
    .replace(/^what does it mean\s+/i, '')
    .replace(/^tell me the meaning of\s+/i, '')
    .replace(/^give me the meaning of\s+/i, '')
    .replace(/^tell me definition of\s+/i, '')
    .replace(/^give me definition of\s+/i, '')
    .replace(/^please define\s+/i, '')
    .replace(/^show me the meaning of\s+/i, '')
    .replace(/^show me definition of\s+/i, '')
    // Synonym and usage questions: the entry lists similar words and examples
    .replace(/^(another|other|a)\s+words?\s+for\s+/i, '')
    .replace(/^synonyms?\s+(for|of)\s+/i, '')
    .replace(/^how (do|can|would|should) (you|i|we) use\s+/i, '')
    .replace(/^use\s+/i, '')
    .replace(/\s+in a sentence\W*$/i, '')
    // English phrases (middle or end)
    .replace(/\bmeaning of\b/i, '')
    .replace(/\bdefinition of\b/i, '')
    .replace(/\bdefination of\b/i, '')
    .replace(/\bmeaning\b/i, '')
    .replace(/\bdefinition\b/i, '')
    .replace(/\bdefination\b/i, '')
    .replace(/\bmeans\b/i, '')
    .replace(/\bmean\b/i, '')
    .replace(/\bexplain\b/i, '')
    .replace(/\bexplanation\b/i, '')
    // Common endings
    .replace(/\s+meaning$/i, '')
    .replace(/\s+definition$/i, '')
    .replace(/\s+defination$/i, '')
    .replace(/\s+means$/i, '')
    .replace(/\s+mean$/i, '')
    // Nepali-English mixed phrases
    .replace(/\bko meaning\b/i, '')
    .replace(/\bko definition\b/i, '')
    .replace(/\bko defination\b/i, '')
    .replace(/\bko arth\b/i, '')
    .replace(/\bko matlab\b/i, '')
    .replace(/\bko matlab bata\b/i, '')
    .replace(/\bko meaning bata\b/i, '')
    .replace(/\bko arth bata\b/i, '')
    .replace(/\bko byakhya\b/i, '')
    .replace(/\bko barne\b/i, '')
    // Cleanup: keep letters, hyphens and apostrophes (well-being, o'clock)
    .replace(/[^a-zA-Z\s'-]/g, '')
    .trim();

  // If multiple words remain, take the last one (usually the target word)
  const words = cleaned.split(/\s+/);
  return words[words.length - 1];
};

// Runs a search from anywhere (example chips, question cards).
// Returns false when there is no word to look up.
export const useDefineSearch = () => {
  const navigate = useNavigate();
  return useCallback((searchQuery) => {
    const word = searchQuery.trim() ? extractWord(searchQuery) : '';
    if (!word) return false;
    navigate(`/define/${encodeURIComponent(word)}`);
    return true;
  }, [navigate]);
};

const SpeechRecognition =
  typeof window !== 'undefined' ? window.SpeechRecognition || window.webkitSpeechRecognition : null;

/**
 * variant="hero":  the large homepage search
 * variant="field": a regular search box for inner pages
 * compact:         drops the search icon and voice button for narrow columns
 */
const SearchBar = ({ variant = 'field', className = '', placeholder, autoFocus = false, compact = false }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [error, setError] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const runSearch = useDefineSearch();
  const searchRef = useRef(null);
  const recognitionRef = useRef(null);
  const inputId = useId();
  const listId = useId();
  const isHero = variant === 'hero';

  const handleSearch = (searchQuery = query) => {
    if (runSearch(searchQuery)) {
      setQuery('');
      setShowSuggestions(false);
      setActiveIndex(-1);
      setError('');
    } else {
      setError('Type a word to look up, like “serendipity”.');
    }
  };

  const stopVoiceSearch = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
      setIsTranscribing(false);
    }
  };

  // Initialize speech recognition
  useEffect(() => {
    if (!SpeechRecognition) return undefined;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setIsListening(true);
      setIsTranscribing(false);
    };

    recognition.onresult = (event) => {
      setIsTranscribing(true);
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      // Update query with real-time transcription
      setQuery(finalTranscript + interimTranscript);

      // Auto-search when final result is available
      if (finalTranscript && finalTranscript.trim()) {
        setTimeout(() => {
          handleSearch(finalTranscript.trim());
          stopVoiceSearch();
        }, 500);
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      setIsTranscribing(false);
      if (event.error === 'not-allowed') {
        setError('Microphone access is blocked. Allow it in your browser, or type the word.');
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      setIsTranscribing(false);
    };

    recognitionRef.current = recognition;
    return () => recognition.abort();
  }, []);

  // Get suggestions when query changes
  useEffect(() => {
    const getSuggestions = async () => {
      if (query.length > 1 && !isListening) {
        const results = await DictionaryService.searchSuggestions(query);
        setSuggestions(results);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
      setActiveIndex(-1);
    };

    const timeoutId = setTimeout(getSuggestions, 200);
    return () => clearTimeout(timeoutId);
  }, [query, isListening]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const listOpen = showSuggestions && suggestions.length > 0 && !isListening;

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSearch(listOpen && activeIndex >= 0 ? suggestions[activeIndex] : query);
  };

  // Arrow keys move through suggestions, Escape closes them
  const handleKeyDown = (e) => {
    if (!listOpen) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setActiveIndex(-1);
    }
  };

  const handleVoiceToggle = () => {
    if (!recognitionRef.current) return;
    if (isListening) {
      stopVoiceSearch();
    } else {
      setQuery('');
      setError('');
      setIsListening(true);
      recognitionRef.current.start();
    }
  };

  const inputProps = {
    id: inputId,
    type: 'text',
    value: query,
    onChange: (e) => {
      setQuery(e.target.value);
      if (error) setError('');
    },
    onKeyDown: handleKeyDown,
    autoComplete: 'off',
    autoCapitalize: 'none',
    spellCheck: 'false',
    autoFocus,
    role: 'combobox',
    'aria-expanded': listOpen,
    'aria-controls': listId,
    'aria-autocomplete': 'list',
    'aria-activedescendant': listOpen && activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined,
    'aria-invalid': Boolean(error),
    'data-testid': 'search-input',
  };

  const listeningPlaceholder = isTranscribing ? 'Listening…' : 'Say a word';
  const voiceButton = SpeechRecognition && !compact && (
    <button
      type="button"
      onClick={handleVoiceToggle}
      className={`grid shrink-0 place-items-center rounded-full transition-colors ${
        isHero ? 'h-12 w-12' : 'h-10 w-10'
      } ${isListening ? 'bg-ink text-paper' : 'text-ink-soft hover:bg-ink/5 hover:text-ink'}`}
      aria-label={isListening ? 'Stop voice search' : 'Search by voice'}
      aria-pressed={isListening}
      data-testid="voice-search-button"
    >
      {isListening ? <MicOff size={isHero ? 22 : 20} aria-hidden="true" /> : <Mic size={isHero ? 22 : 20} aria-hidden="true" />}
    </button>
  );

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      <form role="search" onSubmit={handleSubmit}>
        <label htmlFor={inputId} className="sr-only">Word to define</label>

        {isHero ? (
          <div className="flex h-16 items-center gap-1 rounded-full border border-ink/10 bg-surface pl-5 pr-2 shadow-[0_18px_50px_-20px_rgb(var(--shade)/0.35)] transition-[border-color,box-shadow] duration-300 focus-within:border-ink/40 focus-within:shadow-[0_22px_60px_-18px_rgb(var(--shade)/0.45)] sm:h-[4.5rem] sm:gap-2 sm:pl-6">
            <Search size={22} className="shrink-0 text-ink-soft" aria-hidden="true" />
            <input
              {...inputProps}
              placeholder={isListening ? listeningPlaceholder : placeholder || 'Search a word or ask a question…'}
              className="h-full min-w-0 flex-1 bg-transparent px-1 text-lg text-ink outline-none placeholder:text-ink-soft/80 focus-visible:outline-none sm:text-xl"
            />
            {voiceButton}
            <button
              type="submit"
              className="h-12 shrink-0 rounded-full bg-tangerine px-5 text-base font-semibold text-tangerine-ink transition-[filter,transform] duration-200 hover:brightness-105 active:scale-[0.97] sm:h-14 sm:px-7 sm:text-lg"
              data-testid="search-button"
            >
              Define
            </button>
          </div>
        ) : (
          <div className={`flex items-center gap-1 rounded-full border border-rule bg-surface pr-1.5 transition-colors focus-within:border-leaf focus-within:ring-2 focus-within:ring-leaf/20 ${compact ? 'h-12 pl-2' : 'h-14 pl-5'}`}>
            {!compact && <Search size={20} className="shrink-0 text-ink-soft" aria-hidden="true" />}
            <input
              {...inputProps}
              placeholder={isListening ? listeningPlaceholder : placeholder || 'Type a word'}
              className="h-full min-w-0 flex-1 bg-transparent px-2 text-lg text-ink outline-none placeholder:text-ink-soft/70 focus-visible:outline-none"
            />
            {voiceButton}
            <button
              type="submit"
              className={`shrink-0 rounded-full bg-tangerine px-4 text-[0.95rem] font-semibold text-tangerine-ink transition-[filter,transform] hover:brightness-105 active:scale-[0.97] ${compact ? 'h-9' : 'h-11'}`}
              data-testid="search-button"
            >
              Define
            </button>
          </div>
        )}
      </form>

      <p
        aria-live="polite"
        className={
          error
            ? `mt-3 text-[0.95rem] font-medium text-ink ${isHero ? 'text-center' : ''}`
            : isListening
              ? `mt-3 text-[0.95rem] text-ink-soft ${isHero ? 'text-center' : ''}`
              : 'sr-only'
        }
        role={error ? 'alert' : undefined}
      >
        {error || (isListening ? (isTranscribing ? 'Got it, looking it up…' : 'Listening. Say one word.') : '')}
      </p>

      {listOpen && (
        <ul
          id={listId}
          role="listbox"
          aria-label="Suggestions"
          className="absolute left-0 right-0 top-full z-30 mt-2 overflow-hidden rounded-2xl border border-rule bg-surface p-1.5 text-left shadow-[0_18px_40px_-16px_rgb(var(--shade)/0.3)]"
        >
          {suggestions.map((suggestion, index) => (
            <li
              key={suggestion}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === activeIndex}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSearch(suggestion)}
              onMouseEnter={() => setActiveIndex(index)}
              className={`flex cursor-pointer items-center gap-3 rounded-xl px-4 py-2.5 text-lg text-ink transition-colors ${
                index === activeIndex ? 'bg-butter' : ''
              }`}
              data-testid={`suggestion-${index}`}
            >
              <Search size={16} className="shrink-0 text-ink-soft" aria-hidden="true" />
              {suggestion}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SearchBar;
